import _ from 'lodash';
import dayjs from 'dayjs';

export enum DownloadType {
  default,
  multi,
  newSeenOld,
}

interface DownloadParams {
  begin_date?: string;
  end_date?: string;
  agency_uid?: number;
  agencyUid?: number | Array<number>; // 只用于文件名生成，不用于下载报表参数。目前好像只有日结报表、返点返货在用了，确认不用后应该删掉
  [key: string]: any;
}

type DownloadFileNameRenderType = (param: DownloadParams) => string;

interface DownloadConfig {
  name?: string;
  nameRender?: DownloadFileNameRenderType
  multiNameRender?: DownloadFileNameRenderType
  newSeenOldNameRender?: DownloadFileNameRenderType
  mod: string
  action: string
  multiAction?: string
  newSeenOldAction?: string
  ad_platform_type?: 1 | 2 | 3
  isCps?: boolean
}

function commonFileNameRender(name: string, { withAgencyUid = true, extraFileKey = [] as any } = {}): DownloadFileNameRenderType {
  return function (param: DownloadParams): string {
    const { begin_date, end_date, agencyUid, agency_uid } = param;
    // 组装需要附加下载参数中的key by fightingliu
    let extraFileName = '';
    if (!_.isEmpty(extraFileKey)) {
      _.map(extraFileKey, (key) => {
        const extra = param[key];
        if (extra) {
          extraFileName += `(${extra})`;
        }
      });
    }
    let agencyUidText: any = agencyUid || agency_uid;
    if (Array.isArray(agencyUidText) && agencyUidText.length > 1) {
      agencyUidText = `${agencyUidText[0]}等`;
    }
    if (!begin_date && !end_date) {
      const date = dayjs().format('YYYY-MM-DD');
      if (withAgencyUid && (agencyUid || (agency_uid && +agency_uid))) {
        return `${name}${extraFileName}${date}(${agencyUidText})`;
      }
      return `${name}${extraFileName}${date}`;
    }
    if (begin_date && end_date && begin_date !== end_date) {
      if (withAgencyUid && (agencyUid || (agency_uid && +agency_uid))) {
        return `${name}${extraFileName}${begin_date}至${end_date}(${agencyUidText})`;
      }
      return `${name}${extraFileName}${begin_date}至${end_date}`;
    }
    const date = begin_date || end_date || dayjs().format('YYYY-MM-DD');
    if (withAgencyUid && (agencyUid || (agency_uid && +agency_uid))) {
      return `${name}${extraFileName}${date}(${agencyUidText})`;
    }
    return `${name}${extraFileName}${date}`;
  };
}

export const downloadConfig: { [key: string]: DownloadConfig } = {
  account_gdt_advertising: {
    nameRender: commonFileNameRender('ADQ营销账户列表'),
    multiNameRender: commonFileNameRender('ADQ营销账户列表批量下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_list',
    multiAction: 'multi_get_list',
    ad_platform_type: 1,
  },
  advertiser_get_auth_list: {
    nameRender: commonFileNameRender('授权开户记录'),
    mod: 'advertiser',
    action: 'get_auth_list',
  },
  account_cps_advertising: {
    nameRender: commonFileNameRender('CPS商品营销账户列表'),
    multiNameRender: commonFileNameRender('CPS商品营销账户列表批量下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_list',
    multiAction: 'multi_get_list',
    ad_platform_type: 1,
    isCps: true,
  },
  account_gdt_fastAddAccount: {
    // name: 'ADQ营销账{begin_date}至{end_date}',
    nameRender: commonFileNameRender('极速开户历史列表'),
    mod: 'advertiser',
    action: 'get_copy_list',
    multiAction: 'multi_get_list',
  },
  account_gdt_accountlist: {
    nameRender: commonFileNameRender('ADQ营销投放平台'),
    multiNameRender: commonFileNameRender('ADQ营销投放平台批量下载', { withAgencyUid: false }),
    newSeenOldNameRender: commonFileNameRender('ADQ投放平台（迁移前数据下载）', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_agency_rpt_list',
    multiAction: 'get_all_agency_rpt_list',
    newSeenOldAction: 'get_new_and_old_agency_rpt_list',
    ad_platform_type: 1,
  },
  account_delivery_report: {
    nameRender: commonFileNameRender('ADQ营销投放平台'),
    mod: 'advertiser',
    action: 'get_agency_new_rpt_list',
  },
  account_cps_accountlist: {
    nameRender: commonFileNameRender('CPS商品营销投放平台'),
    multiNameRender: commonFileNameRender('CPS商品营销投放平台批量下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_list',
    multiAction: 'multi_get_list',
    ad_platform_type: 1,
    isCps: true,
  },
  account_wechat: {
    nameRender: commonFileNameRender('WX旧营销账户列表'),
    multiNameRender: commonFileNameRender('WX旧营销账户列表批量下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_wechat_list',
    ad_platform_type: 2,
    multiAction: 'get_wechat_list_by_agency_uid_list',
  },
  account_advertiser_list: {
    nameRender: commonFileNameRender('WX旧营销账户列表'),
    multiNameRender: commonFileNameRender('WX旧营销账户列表批量下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_advertiser_list',
    ad_platform_type: 2,
    multiAction: 'multi_get_advertiser_list',
  },
  account_wechatrpt: {
    nameRender: commonFileNameRender('WX旧营销投放平台'),
    multiNameRender: commonFileNameRender('WX旧营销投放平台批量下载'),
    mod: 'advertiser',
    action: 'get_wechat_agency_rpt_list',
    multiAction: 'get_all_agency_rpt_list',
    ad_platform_type: 2,
  },
  account_wechatlog: {
    nameRender: commonFileNameRender('WX旧营销绑定记录'),
    mod: 'advertiser',
    action: 'get_wechat_auth_log_list',
  },
  report_advertise: {
    nameRender: commonFileNameRender('账户维度基础报表'),
    mod: 'report',
    action: 'get_advertiser_rpt_list',
  },
  report_advertise_v2: {
    nameRender: commonFileNameRender('账户维度明细报表'),
    mod: 'report',
    action: 'get_advertiser_rpt_list_v2',
  },
  report_customer: {
    nameRender: commonFileNameRender('客户维度报表'),
    mod: 'report',
    action: 'get_customer_rpt_list',
  },
  report_ad_rpt: {
    nameRender: commonFileNameRender('营销单元维度报表'),
    mod: 'report',
    action: 'get_ad_rpt_list',
  },
  report_ad_rpt_compaign: {
    nameRender: commonFileNameRender('计划维度报表'),
    mod: 'report',
    action: 'get_ad_rpt_list',
  },
  report_agency_basic: {
    nameRender: commonFileNameRender('服务商维度基础报表'),
    mod: 'report',
    action: 'get_ad_base_rpt_list',
  },
  report_agency_business: {
    nameRender: commonFileNameRender('服务商维度业务报表'),
    mod: 'report',
    action: 'get_base_rpt_list',
  },
  ctr_cvr_report_customer_report: {
    nameRender: commonFileNameRender('服务商两率报表'),
    mod: 'ctr_cvr_report',
    action: 'customer_report',
  },
  chance_v3_custom_list: {
    nameRender: commonFileNameRender('机会客户列表'),
    mod: 'opportunity',
    action: 'advertiser_get_list',
  },
  chance_v3_account_list_dialog_list: {
    nameRender: commonFileNameRender('洞察有提升指标列表'),
    mod: 'opportunity',
    action: 'adv_get_list',
  },
  polling_check_preview_download: {
    nameRender: commonFileNameRender('开户预审巡检'),
    mod: 'inspect',
    action: 'open_account_get_list',
  },
  polling_check_backlink_download: {
    nameRender: commonFileNameRender('素材在投后链路巡查巡检'),
    mod: 'inspect',
    action: 'material_progress_track_get_list',
  },
  finance_report_agency: {
    nameRender: commonFileNameRender('消耗报表服务商维度'),
    mod: 'report',
    action: 'get_cost_rpt_list',
  },
  finance_report_advertiser: {
    nameRender: commonFileNameRender('消耗报表账户维度'),
    mod: 'report',
    action: 'get_cost_rpt_list',
  },
  finance_report_compaign: {
    nameRender: commonFileNameRender('消耗报表计划维度'),
    mod: 'report',
    action: 'get_cost_rpt_list',
  },
  finance_report_ad: {
    nameRender: commonFileNameRender('消耗报表营销单元维度'),
    mod: 'report',
    action: 'get_cost_rpt_list',
  },
  finance_gdt_info: {
    nameRender: commonFileNameRender('财务信息ADQ营销'),
    multiNameRender: commonFileNameRender('财务信息ADQ营销跨服务商下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_list',
    multiAction: 'multi_get_list',
    ad_platform_type: 1,
  },
  finance_wx_info_new: {
    nameRender: commonFileNameRender('财务信息WX旧营销'),
    multiNameRender: commonFileNameRender('财务信息WX旧营销跨服务商下载', { withAgencyUid: false }),
    mod: 'wechat_account',
    action: 'get_wechat_account_list',
    multiAction: 'get_wechat_account_list_by_agency_uid_list',
    ad_platform_type: 2,
  },
  finance_wx_info_list: {
    nameRender: commonFileNameRender('财务信息WX旧营销'),
    multiNameRender: commonFileNameRender('财务信息WX旧营销跨服务商下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_advertiser_list',
    multiAction: 'multi_get_advertiser_list',
    ad_platform_type: 2,
  },
  finance_wx_info: {
    nameRender: commonFileNameRender('财务信息WX旧营销'),
    mod: 'advertiser',
    action: 'get_wechat_agency_rpt_list',
  },
  finance_cps_info: {
    nameRender: commonFileNameRender('财务信息CPS商品营销'),
    multiNameRender: commonFileNameRender('财务信息CPS商品营销跨服务商下载', { withAgencyUid: false }),
    mod: 'advertiser',
    action: 'get_list',
    multiAction: 'multi_get_list',
    ad_platform_type: 1,
    isCps: true,
  },
  finance_record_new: {
    nameRender: commonFileNameRender('财务记录ADQ营销'),
    mod: 'finance',
    action: 'invoice',
  },
  finance_record_new_mp: {
    nameRender: commonFileNameRender('财务记录WX旧营销'),
    mod: 'finance',
    action: 'invoice',
  },
  finance_record: {
    nameRender: commonFileNameRender('财务记录ADQ营销'),
    mod: 'account',
    action: 'get_list',
  },
  finance_record_cps: {
    nameRender: commonFileNameRender('财务记录CPS商品营销'),
    mod: 'finance',
    action: 'invoice',
  },
  shared_wallet_list: {
    nameRender: commonFileNameRender('共享钱包信息'),
    mod: 'wallet',
    action: 'get_list',
  },
  shared_wallet_record: {
    nameRender: commonFileNameRender('共享钱包财务记录'),
    mod: 'finance',
    action: 'invoice',
  },
  finance_rebate: {
    nameRender: commonFileNameRender('返点返货'),
    mod: 'finance',
    action: 'get-advertiser-rebate-map',
  },
  finance_balance_report_agency: {
    nameRender: commonFileNameRender('服务商财务日结报表'),
    multiNameRender: commonFileNameRender('服务商财务日结报表批量下载', { withAgencyUid: false }),
    mod: 'finance',
    action: 'get-balance-report',
    multiAction: 'get-balance-report',
  },
  finance_balance_report_advertising: {
    nameRender: commonFileNameRender('客户财务日结报表'),
    multiNameRender: commonFileNameRender('客户财务日结报表批量下载', { withAgencyUid: false }),
    mod: 'finance',
    action: 'get-balance-report',
    multiAction: 'get-balance-report',
  },
  finance_credit_bid_repayment: {
    nameRender: commonFileNameRender('信用金ADQ营销竞价待还款账单'),
    mod: 'credit',
    action: 'get_auction_refund_bill_list',
  },
  finance_credit_bid_history: {
    nameRender: commonFileNameRender('信用金ADQ营销竞价历史账单'),
    mod: 'credit',
    action: 'get_auction_history_bill_list',
  },
  finance_credit_bid_record: {
    nameRender: commonFileNameRender('信用金ADQ营销竞价还款记录'),
    mod: 'credit',
    action: 'get_auction_refund_record_bill_list',
  },
  finance_credit_contract_repayment: {
    nameRender: commonFileNameRender('信用金ADQ营销合约待还款账单'),
    mod: 'credit',
    action: 'get_contract_refund_bill_list',
  },
  finance_credit_contract_history: {
    nameRender: commonFileNameRender('信用金ADQ营销合约历史账单'),
    mod: 'credit',
    action: 'get_contract_history_bill_list',
  },
  finance_credit_contract_record: {
    nameRender: commonFileNameRender('信用金ADQ营销合约还款记录'),
    mod: 'credit',
    action: 'get_contract_refund_record_bill_list',
  },
  finance_credit_ims_bid_repayment: {
    nameRender: commonFileNameRender('信用金竞价待还款账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_bid_history: {
    nameRender: commonFileNameRender('信用金竞价历史账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_bid_record: {
    nameRender: commonFileNameRender('信用金竞价还款记录'),
    mod: 'finance',
    action: 'get-ims-refund-record',
  },
  finance_credit_ims_contract_repayment: {
    nameRender: commonFileNameRender('信用金合约待还款账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_contract_history: {
    nameRender: commonFileNameRender('信用金合约历史账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_contract_record: {
    nameRender: commonFileNameRender('信用金合约还款记录'),
    mod: 'finance',
    action: 'get-ims-refund-record',
  },
  finance_credit_ims_mutual_repayment: {
    nameRender: commonFileNameRender('信用金互选待还款账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_mutual_history: {
    nameRender: commonFileNameRender('信用金互选历史账单'),
    mod: 'finance',
    action: 'get-ims-credit-bill',
  },
  finance_credit_ims_mutual_record: {
    nameRender: commonFileNameRender('信用金互选还款记录'),
    mod: 'finance',
    action: 'get-ims-refund-record',
  },
  finance_credit_ims_mutual_detail: {
    nameRender: commonFileNameRender('信用金互选明细账单', { extraFileKey: ['bill_id'] }),
    mod: 'finance',
    action: 'get-ims-credit-bill-detail',
  },
  finance_credit_ims_bid_detail: {
    nameRender: commonFileNameRender('信用金竞价明细账单', { extraFileKey: ['bill_id'] }),
    mod: 'finance',
    action: 'get-ims-credit-bill-detail',
  },
  finance_credit_ims_contract_detail: {
    nameRender: commonFileNameRender('信用金合约明细账单', { extraFileKey: ['bill_id'] }),
    mod: 'finance',
    action: 'get-ims-credit-bill-detail',
  },
  wx_finance_record_advertiser: {
    nameRender: commonFileNameRender('财务记录WX旧营销客户'),
    mod: 'wechat_account',
    action: 'get_advertiser_list',
  },
  wx_finance_record_agency: {
    nameRender: commonFileNameRender('财务记录WX旧营销服务商'),
    mod: 'wechat_account',
    action: 'get_agency_list',
  },
  wx_finance_credit_repayment: {
    nameRender: commonFileNameRender('信用金WX旧营销待还款账单'),
    mod: 'wechat_credit',
    action: 'get_bill_list',
  },
  wx_finance_credit_history: {
    nameRender: commonFileNameRender('信用金WX旧营销历史账单'),
    mod: 'wechat_credit',
    action: 'get_bill_list',
  },
  wx_finance_credit_record: {
    nameRender: commonFileNameRender('信用金WX旧营销还款记录'),
    mod: 'wechat_credit',
    action: 'get_refund_record_bill_list',
  },
  manage_role: {
    nameRender: commonFileNameRender('员工管理服务商员工管理'),
    mod: 'operator',
    action: 'get_list',
  },
  manage_advertiserstaff: {
    nameRender: commonFileNameRender('员工管理客户员工管理'),
    mod: 'operator',
    action: 'get_list',
  },
  employee: {
    nameRender: commonFileNameRender('员工管理'),
    mod: 'employee',
    action: 'get_list',
  },
  advertiserList: {
    nameRender: commonFileNameRender('人户关系'),
    mod: 'operator',
    action: 'get_bind_advertiser_list',
  },
  examDetail: {
    nameRender: commonFileNameRender('课程培训考试详情表'),
    mod: 'edu',
    action: 'get_user_exam_detail',
  },
  trainingDetail: {
    nameRender: commonFileNameRender('课程培训学习详情表'),
    mod: 'edu',
    action: 'get_user_training_detail',
  },
  trainingInfo: {
    nameRender: commonFileNameRender('课程培训学员信息表'),
    mod: 'edu',
    action: 'get_user_subordinate_training_info',
  },
  transfer_record_pending: {
    nameRender: commonFileNameRender('待处理迁移记录', { withAgencyUid: false }),
    mod: 'advertiser_transfer',
    action: 'get_list',
  },
  transfer_record_history: {
    nameRender: commonFileNameRender('历史迁移记录', { withAgencyUid: false }),
    mod: 'advertiser_transfer',
    action: 'get_list',
  },
  account_file_contact: {
    // 帐号档案 联系人下载
    nameRender: commonFileNameRender('账号档案联系人'),
    mod: 'agency_contact',
    action: 'get_list',
  },
  account_file_contact_records: {
    // 帐号档案 联系人下载
    nameRender: commonFileNameRender('账号档案联系记录'),
    mod: 'agency_contact_records',
    action: 'get_list',
  },
  // 任务中心
  task_center_person: {
    nameRender: commonFileNameRender('个人任务中心'),
    mod: 'task',
    action: 'get_list',
  },
  // 任务中心
  task_center_personc_operate: {
    nameRender: commonFileNameRender('个人任务中心'),
    mod: 'task_group',
    action: 'get_list',
  },
  //
  // 客户维度离线下载 agency_dashboard_mdm_rpt_download_async
  task_customer_list: {
    nameRender: commonFileNameRender('腾盟魔方客户维度列表'),
    mod: 'agency_dashboard',
    action: 'mdm_rpt_download_async',
  },
  // 账户维度离线下载 agency_dashboard_advertiser_rpt_download_async
  task_ad_list: {
    nameRender: commonFileNameRender('腾盟魔方账户维度列表', { extraFileKey: ['mdm_name'] }),
    mod: 'agency_dashboard',
    action: 'advertiser_rpt_download_async',
  },
  // 营销单元维度离线下载 agency_dashboard_ad_rpt_download_async
  task_ad_detail: {
    nameRender: commonFileNameRender('腾盟魔方账户维度详情'),
    mod: 'agency_dashboard',
    action: 'ad_rpt_download_async',
  },
  task_center_team: {
    nameRender: commonFileNameRender('团队任务中心'),
    mod: 'task',
    action: 'get_list',
  },
  task_center_team_operate: {
    nameRender: commonFileNameRender('团队任务中心'),
    mod: 'task_group',
    action: 'get_list',
  },
  // 子客违规查询下载
  punishment_list: {
    nameRender: commonFileNameRender('子客违规查询'),
    mod: 'punish',
    action: 'get_list',
  },
  agency_finance: {
    nameRender: commonFileNameRender('服务商余额详情报表'),
    mod: 'agency_finance',
    action: 'balance_rpt_list',
  },
  agency_customer: {
    nameRender: commonFileNameRender('子客余额详情报表'),
    mod: 'agency_customer',
    action: 'balance_rpt_list',
  },
  agency_present: {
    nameRender: commonFileNameRender('赠送金详情报表'),
    mod: 'agency_finance',
    action: 'balance_rpt_list',
  },
  // 分发池下载列表
  distribution_pool: {
    nameRender: commonFileNameRender('分配服务商线索池'),
    mod: 'punishment',
    action: 'search',
  },
  // 小时报表下载
  hourly_report_list: {
    nameRender: commonFileNameRender('ADQ投放平台小时报表'),
    mod: 'report',
    action: 'get_user_hourly_report_list',
  },
  task_dashboard_finish_industry: {
    nameRender: commonFileNameRender('任务看板客户行业维度已结束任务'),
    mod: 'task',
    action: 'get_dashboard_industry_finished_list',
  },
  task_dashboard_ongoing_industry: {
    nameRender: commonFileNameRender('任务看板客户行业维度任务期任务'),
    mod: 'task',
    action: 'get_dashboard_industry_ongoing_list',
  },
  task_dashboard_finish_archi: {
    nameRender: commonFileNameRender('任务看板组织架构维度已结束任务'),
    mod: 'task',
    action: 'get_dashboard_orbac_finished_list',
  },
  task_dashboard_ongoing_archi: {
    nameRender: commonFileNameRender('任务看板组织架构维度任务期任务'),
    mod: 'task',
    action: 'get_dashboard_orbac_ongoing_list',
  },
  task_dashboard_finish_employee: {
    nameRender: commonFileNameRender('任务看板员工维度已结束任务'),
    mod: 'task',
    action: 'get_dashboard_employee_finished_list',
  },
  task_dashboard_ongoing_employee: {
    nameRender: commonFileNameRender('任务看板员工维度任务期任务'),
    mod: 'task',
    action: 'get_dashboard_employee_ongoing_list',
  },
  task_dashboard_finish_strategy: {
    nameRender: commonFileNameRender('任务看板策略维度已结束任务'),
    mod: 'task',
    action: 'get_dashboard_strategy_finished_list',
  },
  task_dashboard_ongoing_strategy: {
    nameRender: commonFileNameRender('任务看板策略维度任务期任务'),
    mod: 'task',
    action: 'get_dashboard_strategy_ongoing_list',
  },
  task_dashboard_finish_task: {
    nameRender: commonFileNameRender('任务看板已结束明细任务'),
    mod: 'task',
    action: 'get_dashboard_finished_list',
  },
  task_dashboard_ongoing_task: {
    nameRender: commonFileNameRender('任务看板任务期明细任务'),
    mod: 'task',
    action: 'get_dashboard_ongoing_list',
  },
  report_sogou: {
    nameRender: commonFileNameRender('搜狗历史数据报表'),
    mod: 'report',
    action: 'get_sogou_rpt',
  },
  bm_merge_conflict: {
    nameRender: commonFileNameRender('员工冲突数据', { withAgencyUid: false, extraFileKey: ['merge_bm_id'] }),
    mod: 'group_manager',
    action: 'get_user_diff_list',
  },
  report_sogou_base: {
    nameRender: commonFileNameRender('搜狗客户账户基础数据'),
    mod: 'report',
    action: 'get_sogou_ad_base_info',
  },
  report_sogou_account_month: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023按月消耗数据汇总'),
    mod: 'report',
    action: 'get_sogou_account_cost_by_month',
  },
  report_sogou_account_quarter: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023季度任务数据汇总'),
    mod: 'report',
    action: 'get_sogou_account_cost_by_quarter',
  },
  report_sogou_fund1: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务增资记录'),
    mod: 'report',
    action: 'get_sogou_fund_1_list',
  },
  report_sogou_fund2: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务转账客户记录'),
    mod: 'report',
    action: 'get_sogou_fund_2_list',
  },
  report_sogou_fund3: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务转账代理记录'),
    mod: 'report',
    action: 'get_sogou_fund_3_list',
  },
  report_sogou_fund4: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务账户管家转账记录'),
    mod: 'report',
    action: 'get_sogou_fund_4_list',
  },
  report_sogou_fund5: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务返点记录'),
    mod: 'report',
    action: 'get_sogou_fund_5_list',
  },
  report_sogou_fund6: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务预付款扣款记录'),
    mod: 'report',
    action: 'get_sogou_fund_6_list',
  },
  report_sogou_fund7: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务品牌订单转账记录'),
    mod: 'report',
    action: 'get_sogou_fund_7_list',
  },
  report_sogou_fund8: {
    nameRender: commonFileNameRender('搜狗服务商2020-2023财务预存资金订单记录'),
    mod: 'report',
    action: 'get_sogou_fund_8_list',
  },
  // 拓品任务列表
  task_get_spu_list: {
    nameRender: commonFileNameRender('拓品任务'),
    mod: 'task',
    action: 'get_spu_list',
  },
  // 拓客任务列表
  task_dashboard_shop_opportunity: {
    nameRender: commonFileNameRender('拓客任务-系统下发'),
    mod: 'customer_extend',
    action: 'get_list',
  },
  task_dashboard_self_opportunity: {
    nameRender: commonFileNameRender('拓客任务-自拓'),
    mod: 'agency_customer_extend',
    action: 'get_list',
  },
  product_category_advertiser: {
    nameRender: commonFileNameRender('商品分析'),
    mod: 'report',
    action: 'get_product_category_rpt',
  },
  account_group: {
    nameRender: commonFileNameRender('营销账户列表'),
    mod: 'worktable',
    action: 'account_list',
  },
  creative_material_manage_tags: {
    nameRender: commonFileNameRender('素材标签列表'),
    mod: 'eservice_material',
    action: 'get_label_list',
  },
  creative_material_list_video: {
    nameRender: commonFileNameRender('视频素材列表'),
    mod: 'material',
    action: 'media_get_list',
  },
  creative_material_list_image: {
    nameRender: commonFileNameRender('图片素材列表'),
    mod: 'material',
    action: 'image_get_list',
  },
  creative_material_audit_list_video: {
    nameRender: commonFileNameRender('视频素材审核列表'),
    mod: 'material',
    action: 'audit_media_list',
  },
  creative_material_audit_list_image: {
    nameRender: commonFileNameRender('图片素材审核列表'),
    mod: 'material',
    action: 'audit_image_list',
  },
  creative_material_report_owner: {
    nameRender: commonFileNameRender('素材分析人效报表'),
    mod: 'material',
    action: 'user_efficiency_report',
  },
  bm_merge_get_user_conflict_data: {
    nameRender: commonFileNameRender('集团合并员工冲突数据', { extraFileKey: ['merge_bm_id', 'agency_id'] }),
    mod: 'bm_merge',
    action: 'get_user_conflict_data',
  },
  bm_merge_get_licence_conflict_data: {
    nameRender: commonFileNameRender('集团合并服务商资质冲突数据', { extraFileKey: ['merge_bm_id', 'agency_id'] }),
    mod: 'bm_merge',
    action: 'get_licence_conflict_data',
  },
  bm_bind_agency_get_user_conflict_data: {
    nameRender: commonFileNameRender('账号认领员工冲突数据', { extraFileKey: ['merge_bm_id', 'agency_id'] }),
    mod: 'bm_bind_agency',
    action: 'get_user_conflict_data',
  },
  bm_bind_agency_get_licence_conflict_data: {
    nameRender: commonFileNameRender('账号认领服务商资质冲突数据', { extraFileKey: ['merge_bm_id', 'agency_id'] }),
    mod: 'bm_bind_agency',
    action: 'get_licence_conflict_data',
  },
  agency_gen1_account_get_list: {
    nameRender: commonFileNameRender('主体服务商账号列表'),
    mod: 'agency',
    action: 'gen1_account_get_list',
  },
  agency_gen2_account_get_list: {
    nameRender: commonFileNameRender('主体服务商子账号列表'),
    mod: 'agency',
    action: 'gen2_account_get_list',
  },
  agency_tech_account_get_list: {
    nameRender: commonFileNameRender('技术服务商账号列表'),
    mod: 'agency',
    action: 'tech_account_get_list',
  },
  creative_aigc_excitation: {
    nameRender: commonFileNameRender('激励创意列表'),
    mod: 'creative',
    action: 'get_creative_aigc_excitation_list',
  },
  growup_professional: {
    mod: 'grow_up_center',
    action: 'talent_cert_list',
  },
  growup_advertising: {
    mod: 'grow_up_center',
    action: 'ability_short_advertiser_name_list',
  },
  growup_operate: {
    mod: 'grow_up_center',
    action: 'operate_ability_short_advertiser_name_list',
  },
  growup_risk: {
    mod: 'grow_up_center',
    action: 'illegal_entity_list',
  },
  growup_trend: {
    mod: 'grow_up_center',
    action: 'history_list',
  },
  incentive_assess_list: {
    mod: 'incentive_center',
    action: 'customer_list',
  },
  passive_accumulation_report: {
    nameRender: (params) => `被动累量列表-${params.frame_type}类客户-${dayjs().format('YYYYMMDD')}`,
    mod: 'passive_accumulation',
    action: 'list',
  },
};
